import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Download, Eye, FileText, Lock, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate } from '@/lib/format';
import type { ApiSuccess } from '@/lib/types';

type SharedView =
  | { needsPassword: true }
  | {
      needsPassword: false;
      document: {
        name: string;
        description: string | null;
        fileName: string | null;
        mimeType: string | null;
        sizeLabel: string | null;
        previewable: boolean;
      };
      sharedBy: string;
      viewOnly: boolean;
      downloadsLeft: number | null;
      expiresAt: string | null;
    };

/**
 * A document shared outside the workspace (spec DOC-10).
 *
 * No session, no app shell. The token in the URL is the credential, and every
 * limit on it — password, expiry, download cap, revocation — is enforced by
 * the server on each request rather than by hiding a button here.
 */
export function PublicSharePage() {
  const { token } = useParams<{ token: string }>();
  const base = import.meta.env.VITE_API_URL ?? '/api/v1';

  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['shared', token, submitted],
    queryFn: async () => {
      const response = await axios.get<ApiSuccess<SharedView>>(
        `${base}/public/shared/${token}`,
        { params: submitted ? { password: submitted } : undefined },
      );
      return response.data.data;
    },
    retry: false,
  });

  const download = useMutation({
    mutationFn: async () => {
      const response = await axios.post<ApiSuccess<{ url: string; filename: string }>>(
        `${base}/public/shared/${token}/download`,
        { password: submitted || undefined },
      );
      return response.data.data;
    },
    onSuccess: (result) => window.open(result.url, '_blank', 'noopener'),
    onError: (caught) =>
      setError(
        axios.isAxiosError(caught)
          ? ((caught.response?.data as { error?: { message?: string } })?.error?.message ??
            'Could not download that file.')
          : 'Could not download that file.',
      ),
  });

  if (isLoading) return <Shell>Loading…</Shell>;

  if (loadError) {
    const message = axios.isAxiosError(loadError)
      ? ((loadError.response?.data as { error?: { message?: string } })?.error?.message ??
        'This link is not valid')
      : 'This link is not valid';

    return (
      <Shell>
        <div className="text-center">
          <XCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">{message}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            It may have expired or been revoked. Ask your contact for a new one.
          </p>
        </div>
      </Shell>
    );
  }

  if (!data) return null;

  if (data.needsPassword) {
    return (
      <Shell>
        <div className="text-center">
          <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <h1 className="text-lg font-semibold">This document is password protected</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter the password you were given.
          </p>
        </div>

        <form
          className="mx-auto mt-6 max-w-xs space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(password);
          }}
        >
          <div className="space-y-1.5 text-left">
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoFocus
            />
          </div>
          <Button type="submit" className="w-full" disabled={password.length === 0}>
            Open document
          </Button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-center">
        <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <h1 className="text-xl font-semibold">{data.document.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Shared with you by {data.sharedBy}
        </p>
      </div>

      {data.document.description && (
        <p className="mt-5 whitespace-pre-line text-sm text-muted-foreground">
          {data.document.description}
        </p>
      )}

      <dl className="mt-6 space-y-2 border-t pt-5 text-sm">
        {data.document.fileName && (
          <Row label="File" value={data.document.fileName} />
        )}
        {data.document.sizeLabel && <Row label="Size" value={data.document.sizeLabel} />}
        {data.expiresAt && (
          <Row label="Link expires" value={formatDate(data.expiresAt)} />
        )}
        {data.downloadsLeft !== null && (
          <Row
            label="Downloads left"
            value={`${data.downloadsLeft} of the limit set`}
          />
        )}
      </dl>

      {error && (
        <p className="mt-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="mt-6 flex justify-center">
        {data.viewOnly ? (
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            <Eye className="h-4 w-4" />
            This document was shared to view, not to download.
          </div>
        ) : data.downloadsLeft === 0 ? (
          <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            This link has reached its download limit.
          </div>
        ) : (
          <Button
            size="lg"
            disabled={download.isPending}
            onClick={() => {
              setError(null);
              download.mutate();
            }}
          >
            <Download className="h-4 w-4" />
            {download.isPending ? 'Preparing…' : 'Download'}
          </Button>
        )}
      </div>
    </Shell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-10">
      <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-sm sm:p-8">
        {children}
      </div>
    </div>
  );
}
